import { BlockTool, BlockToolData, API } from '@editorjs/editorjs';
import type { MenuConfig } from '@editorjs/editorjs/types/tools';

interface ButtonData extends BlockToolData {
  text: string;
  url: string;
  bgColor: string;
  textColor: string;
}

export default class ButtonTool implements BlockTool {
  private api: API;
  private data: ButtonData;
  private wrapper: HTMLElement;
  private editForm: HTMLElement;
  private textInput: HTMLInputElement;
  private urlInput: HTMLInputElement;
  private bgColorInput: HTMLInputElement;
  private textColorInput: HTMLInputElement;
  private buttonElement: HTMLElement;

  static get toolbox() {
    return {
      title: 'Button',
      icon: '<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="16" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><text x="10" y="12" text-anchor="middle" font-size="8" fill="currentColor">B</text></svg>'
    };
  }

  constructor({ data, api }: { data: ButtonData; api: API }) {
    this.api = api;

    this.data = {
      text: data.text || 'Enter button text',
      url: data.url || '',
      bgColor: data.bgColor || '#000000',
      textColor: data.textColor || '#FFFFFF'
    };
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.className = 'button-tool';

    // Create preview container (always visible)
    const previewContainer = document.createElement('div');
    previewContainer.className = 'button-preview-container';

    this.buttonElement = document.createElement('div');
    this.buttonElement.className = 'button button-preview';
    this.buttonElement.textContent = this.data.text || 'Enter button text';
    this.buttonElement.style.backgroundColor = this.data.bgColor;
    this.buttonElement.style.color = this.data.textColor;
    this.buttonElement.style.padding = '10px 20px';
    this.buttonElement.style.borderRadius = '4px';
    this.buttonElement.style.textDecoration = 'none';
    this.buttonElement.style.display = 'inline-block';
    this.buttonElement.style.cursor = 'pointer';
    this.buttonElement.addEventListener('click', (e) => {
      e.preventDefault();
      this.showEditForm();
    });

    previewContainer.appendChild(this.buttonElement);

    // Create edit form (will be added to DOM only when needed)
    const editForm = document.createElement('div');
    editForm.className = 'button-edit-form';

    const textLabel = document.createElement('label');
    textLabel.textContent = 'Button Text:';
    this.textInput = document.createElement('input');
    this.textInput.type = 'text';
    this.textInput.value = this.data.text;
    this.textInput.placeholder = 'Enter button text';
    this.textInput.className = 'button-text-input';
    this.textInput.addEventListener('input', () => {
      this.data.text = this.textInput.value;
      this.updateButtonDisplay();
    });

    const urlLabel = document.createElement('label');
    urlLabel.textContent = 'URL:';
    this.urlInput = document.createElement('input');
    this.urlInput.type = 'url';
    this.urlInput.value = this.data.url;
    this.urlInput.placeholder = 'https://example.com';
    this.urlInput.className = 'button-url-input';
    this.urlInput.addEventListener('input', () => {
      this.data.url = this.urlInput.value;
      this.updateButtonDisplay();
    });

    // Background color picker
    const bgColorLabel = document.createElement('label');
    bgColorLabel.textContent = 'Background Color:';
    this.bgColorInput = document.createElement('input');
    this.bgColorInput.type = 'color';
    this.bgColorInput.value = this.data.bgColor;
    this.bgColorInput.className = 'button-bg-color-input';
    this.bgColorInput.addEventListener('input', () => {
      this.data.bgColor = this.bgColorInput.value;
      this.updateButtonDisplay();
    });

    // Text color picker
    const textColorLabel = document.createElement('label');
    textColorLabel.textContent = 'Text Color:';
    this.textColorInput = document.createElement('input');
    this.textColorInput.type = 'color';
    this.textColorInput.value = this.data.textColor;
    this.textColorInput.className = 'button-text-color-input';
    this.textColorInput.addEventListener('input', () => {
      this.data.textColor = this.textColorInput.value;
      this.updateButtonDisplay();
    });

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'button-form-actions';

    const doneButton = document.createElement('button');
    doneButton.textContent = 'Done';
    doneButton.className = 'button-done-btn';
    doneButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.hideEditForm();
    });

    const deleteButton = document.createElement('button');
    deleteButton.textContent = 'Delete Button';
    deleteButton.className = 'button-delete-btn';
    deleteButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (confirm('Are you sure you want to delete this button?')) {
        this.api.blocks.delete();
      }
    });

    buttonContainer.appendChild(doneButton);
    buttonContainer.appendChild(deleteButton);

    editForm.appendChild(textLabel);
    editForm.appendChild(this.textInput);
    editForm.appendChild(urlLabel);
    editForm.appendChild(this.urlInput);
    editForm.appendChild(bgColorLabel);
    editForm.appendChild(this.bgColorInput);
    editForm.appendChild(textColorLabel);
    editForm.appendChild(this.textColorInput);
    editForm.appendChild(buttonContainer);

    // Store edit form but don't add to DOM initially
    this.editForm = editForm;

    // Only add preview container initially
    this.wrapper.appendChild(previewContainer);

    return this.wrapper;
  }

  private showEditForm() {
    const previewContainer = this.wrapper.querySelector('.button-preview-container');

    if (previewContainer) {
      // Remove preview from DOM instead of hiding
      previewContainer.remove();
    }

    // Add edit form to DOM
    this.wrapper.appendChild(this.editForm);
    this.textInput.focus();
  }

  private hideEditForm() {
    const previewContainer = this.wrapper.querySelector('.button-preview-container');

    // Remove edit form from DOM instead of hiding
    this.editForm.remove();

    // Re-add preview container if not present
    if (!previewContainer) {
      const newPreviewContainer = document.createElement('div');
      newPreviewContainer.className = 'button-preview-container';
      newPreviewContainer.appendChild(this.buttonElement);
      this.wrapper.appendChild(newPreviewContainer);
    }
  }

  private updateButtonDisplay() {
    if (this.buttonElement) {
      this.buttonElement.textContent = this.data.text || 'Enter button text';
      this.buttonElement.style.backgroundColor = this.data.bgColor;
      this.buttonElement.style.color = this.data.textColor;
    }
  }

  save(): ButtonData {
    return {
      text: this.textInput.value,
      url: this.urlInput.value,
      bgColor: this.data.bgColor,
      textColor: this.data.textColor
    };
  }

  renderSettings(): MenuConfig {
    return [
      {
        icon: '<svg width="20" height="20" viewBox="0 0 20 20"><path d="M12.854 2.146a.5.5 0 0 1 0 .708l-11 11a.5.5 0 0 1-.708-.708l11-11a.5.5 0 0 1 .708 0z" fill="currentColor"/><rect x="11" y="0" width="2" height="4" fill="currentColor"/><rect x="14" y="3" width="2" height="4" transform="rotate(90 15 5)" fill="currentColor"/></svg>',
        title: 'Edit Button',
        onActivate: () => {
          this.showEditForm();
        }
      },
      {
        icon: '<svg width="20" height="20" viewBox="0 0 20 20"><path d="M2 2 L18 18 M18 2 L2 18" stroke="currentColor" stroke-width="2"/></svg>',
        title: 'Remove Button',
        onActivate: () => {
          this.api.blocks.delete();
        }
      }
    ];
  }

  static get pasteConfig() {
    return {
      tags: ['A']
    };
  }

  onPaste(event: CustomEvent<{ data: HTMLElement }>) {
    const link = event.detail.data;
    if (link && link.tagName === 'A') {
      this.data.text = (link as HTMLAnchorElement).textContent || 'Enter button text';
      this.data.url = (link as HTMLAnchorElement).href || '';
    }
  }
} 
